import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FileImage } from "@/components/FileImage";
import { ImageUpload } from "@/components/ImageUpload";
import { HUBS } from "@/lib/brand";
import { Loader } from "@/components/Loader";

const EMPTY = { title: "", city: HUBS[0], datetime_str: "", description: "", public_venue: "", private_venue: "", member_price: "", guest_price: "", capacity: 0, rsvp_deadline: "", image_path: null };

export default function AdminEvents() {
  const { t } = useApp();
  const [events, setEvents] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [rsvpFor, setRsvpFor] = useState(null);
  const [rsvps, setRsvps] = useState([]);

  const load = () => api.get("/admin/events").then(({ data }) => setEvents(data.events)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (e) => { setForm({ ...EMPTY, ...e }); setEditing(e.id); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const payload = { ...form, capacity: parseInt(form.capacity) || 0 };
    try {
      if (editing === "new") await api.post("/admin/events", payload);
      else await api.put(`/admin/events/${editing}`, payload);
      toast.success(t("saved")); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete event?")) return; await api.delete(`/admin/events/${id}`); toast.success(t("delete")); load(); };

  const viewRsvps = async (e) => {
    setRsvpFor(e);
    const { data } = await api.get(`/admin/events/${e.id}/rsvps`);
    setRsvps(data.rsvps);
  };
  const confirmRsvp = async (id) => { await api.post(`/admin/rsvps/${id}/confirm`); toast.success(t("confirm")); setRsvps((r) => r.map((x) => x.id === id ? { ...x, status: "confirmed" } : x)); };

  const inputCls = "cu-input w-full px-3 py-2.5 text-sm";

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-cu text-3xl">{t("events")}</h1>
        <button onClick={openNew} data-testid="admin-create-event-button" className="btn-gold px-5 py-2.5 text-xs flex items-center gap-2"><Plus size={15} /> {t("create_event")}</button>
      </div>

      {events === null ? <Loader /> : events.length === 0 ? <p className="text-sm text-[color:var(--cu-muted)] py-10 text-center">{t("no_events")}</p> : (
        <div className="space-y-3">
          {events.map((e) => (
            <div key={e.id} className="cu-card p-4 flex items-center gap-4" data-testid={`admin-event-${e.id}`}>
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-[rgba(212,175,55,0.06)]">
                {e.image_path && <FileImage path={e.image_path} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif-cu text-[color:var(--cu-text)] truncate">{e.title}</div>
                <div className="eyebrow mt-1">{e.city} · {e.datetime_str} · {e.rsvp_count} RSVP</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <IconBtn onClick={() => viewRsvps(e)} testid={`event-rsvps-${e.id}`}><Users size={15} /></IconBtn>
                <IconBtn onClick={() => openEdit(e)} testid={`event-edit-${e.id}`}><Pencil size={15} /></IconBtn>
                <IconBtn onClick={() => del(e.id)} testid={`event-delete-${e.id}`}><Trash2 size={15} className="text-red-400" /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[color:var(--cu-charcoal)] border-[color:var(--cu-border)] text-[color:var(--cu-text)] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader><DialogTitle className="font-serif-cu text-xl">{editing === "new" ? t("create_event") : t("edit")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Lbl l={t("title")}><input data-testid="event-form-title" value={form.title} onChange={set("title")} className={inputCls} /></Lbl>
            <div className="grid grid-cols-2 gap-3">
              <Lbl l={t("city")}><select value={form.city} onChange={set("city")} className={inputCls}>{HUBS.map((h) => <option key={h} className="bg-[color:var(--cu-charcoal)]">{h}</option>)}</select></Lbl>
              <Lbl l={t("datetime")}><input data-testid="event-form-datetime" value={form.datetime_str} onChange={set("datetime_str")} placeholder="12 Jul 2026, 20:00" className={inputCls} /></Lbl>
            </div>
            <Lbl l={t("description")}><textarea value={form.description} onChange={set("description")} rows={3} className={`${inputCls} resize-none`} /></Lbl>
            <Lbl l={t("public_venue")}><input value={form.public_venue} onChange={set("public_venue")} className={inputCls} /></Lbl>
            <Lbl l={t("private_venue")}><input value={form.private_venue} onChange={set("private_venue")} className={inputCls} /></Lbl>
            <div className="grid grid-cols-2 gap-3">
              <Lbl l={t("member_price")}><input value={form.member_price} onChange={set("member_price")} placeholder="€50" className={inputCls} /></Lbl>
              <Lbl l={t("guest_price")}><input value={form.guest_price} onChange={set("guest_price")} placeholder="€75" className={inputCls} /></Lbl>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl l={t("capacity")}><input type="number" value={form.capacity} onChange={set("capacity")} className={inputCls} /></Lbl>
              <Lbl l={t("rsvp_deadline")}><input value={form.rsvp_deadline} onChange={set("rsvp_deadline")} placeholder="10 Jul 2026" className={inputCls} /></Lbl>
            </div>
            <Lbl l={t("image")}>
              <ImageUpload value={form.image_path} onChange={(path) => setForm((f) => ({ ...f, image_path: path }))} testId="event-image-upload" />
            </Lbl>
            <button onClick={save} disabled={saving} data-testid="event-form-save" className="btn-gold w-full py-3 text-xs mt-2">{t("save")}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RSVP viewer */}
      <Dialog open={!!rsvpFor} onOpenChange={(o) => !o && setRsvpFor(null)}>
        <DialogContent className="bg-[color:var(--cu-charcoal)] border-[color:var(--cu-border)] text-[color:var(--cu-text)] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader><DialogTitle className="font-serif-cu text-xl">{rsvpFor?.title} — {t("event_rsvps")}</DialogTitle></DialogHeader>
          {rsvps.length === 0 ? <p className="text-sm text-[color:var(--cu-muted)]">No requests yet.</p> : (
            <div className="space-y-2">
              {rsvps.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 cu-card p-3">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{r.member_name}</div>
                    <div className="eyebrow truncate">{r.membership_number || r.email}</div>
                  </div>
                  {r.status === "confirmed" ? <span className="text-[0.65rem] text-emerald-400 font-mono-cu uppercase">{t("rsvp_confirmed")}</span>
                    : <button onClick={() => confirmRsvp(r.id)} data-testid={`confirm-rsvp-${r.id}`} className="btn-gold px-3 py-1.5 text-[0.65rem]">{t("confirm")}</button>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Lbl({ l, children }) { return <div><label className="eyebrow block mb-1.5">{l}</label>{children}</div>; }
function IconBtn({ children, onClick, testid }) { return <button data-testid={testid} onClick={onClick} className="w-9 h-9 flex items-center justify-center rounded-lg gold-border hover:bg-[rgba(212,175,55,0.08)] text-[color:var(--cu-gold-light)]">{children}</button>; }
